import { Box, Button, Dialog, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getNavigationBlockDialogIsShown } from 'features/NavigationBlockModal/model/selectors/navigationBlockSelectors';
import { navigationBlockModalActions } from 'features/NavigationBlockModal/model/slices/NavigationBlockModalSlice';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';

import { useNavigationBlock } from '../NavigationBlockProvider/NavigationBlockProvider';

export interface NavigationBlockConfig {
    title?: string;
    message?: string;
    confirmQuestion?: string;
    cancelButtonText?: string;
    submitButtonText?: string;
    onCancel?: () => void;
    onSubmit?: () => void;
}

interface NavigationBlockModalProps {
    config?: NavigationBlockConfig;
}

export const NavigationBlockModal = memo(
    ({ config }: NavigationBlockModalProps) => {
        const dispatch = useAppDispatch();
        const { proceed } = useNavigationBlock();
        const dialogIsShown = useSelector(getNavigationBlockDialogIsShown);
        const { t } = useTranslation();

        // Default configuration memoized to prevent re-creating on every render
        const finalConfig = useMemo(() => {
            const defaultConfig: NavigationBlockConfig = {
                title: t('Cancel Changes'),
                message: t(
                    'You have unsaved changes. If you navigate away, all changes will be lost.',
                ),
                confirmQuestion: t('Do you want to proceed?'),
                cancelButtonText: t('Keep editing'),
                submitButtonText: t('Cancel changes'),
                onCancel: undefined,
                onSubmit: undefined,
            };
            return { ...defaultConfig, ...config };
        }, [config, t]);

        const handleCancel = useCallback(() => {
            dispatch(navigationBlockModalActions.setShowDialog(false));

            // Only unblock navigation if no custom cancel handler is provided
            if (!finalConfig.onCancel) {
                dispatch(
                    navigationBlockModalActions.setNavigationBlocked(false),
                );
            }

            if (finalConfig.onCancel) {
                finalConfig.onCancel();
            }
        }, [dispatch, finalConfig]);

        const handleSubmit = useCallback(() => {
            if (finalConfig.onSubmit) {
                finalConfig.onSubmit();
            }

            dispatch(navigationBlockModalActions.setNavigationBlocked(false));
            dispatch(navigationBlockModalActions.setShowDialog(false));

            setTimeout(() => {
                proceed();
            }, 0);
        }, [dispatch, finalConfig, proceed]);

        if (!dialogIsShown) {
            return null;
        }

        return (
            <Dialog open onClose={handleCancel}>
                <DialogBoxContent
                    onClose={handleCancel}
                    buttons={
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            width="100%"
                        >
                            <Button onClick={handleCancel}>
                                {finalConfig.cancelButtonText}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                            >
                                {finalConfig.submitButtonText}
                            </Button>
                        </Box>
                    }
                >
                    <Box maxWidth="360px">
                        <Typography mb="21px" typography="poppins.subtitle2">
                            {finalConfig.title}
                        </Typography>
                        <Typography mb="21px" typography="openSans.body2">
                            {finalConfig.message}
                        </Typography>
                        <Typography mb="10px" typography="openSans.body2">
                            {finalConfig.confirmQuestion}
                        </Typography>
                    </Box>
                </DialogBoxContent>
            </Dialog>
        );
    },
);
